import type { GameConfigurator, ConfigSchema } from '../../interfaces/game-type-plugin.interface';
import type { SportsGameSettings } from './sports.types';

/**
 * Sports game configurator — provides default settings and validation.
 *
 * The UI form lives in the portal (SportsConfiguratorComponent).
 * This object handles the programmatic interface used by GameTypeRegistryService:
 *   - getDefaultConfig() — initial values when a new sports campaign is created
 *   - validateConfig()   — pre-save validation rules
 */
export class SportsGameConfigurator implements GameConfigurator {
  getDefaultConfig(): SportsGameSettings {
    return {
      type: 'sports',
      sportType: 'golf-driving',
      attempts: 5,
      assistFactor: 0.4,
      pointsPerSuccess: 5,
      distanceBands: undefined,
      enable3PointLine: false,
      prizeDetermination: 'flat',
      instantPrizeIndex: 0,
      scoreThresholds: undefined,
      showScoreDisplay: true,
    };
  }

  validateConfig(config: unknown): string[] {
    const errors: string[] = [];
    const s = config as SportsGameSettings;

    if (s.type !== 'sports') {
      errors.push('Invalid game type: expected "sports"');
    }

    if (!s.sportType) {
      errors.push('Sport type is required');
    }

    if (s.attempts < 1 || s.attempts > 10) {
      errors.push('Attempts must be between 1 and 10');
    }

    if (s.assistFactor < 0 || s.assistFactor > 1) {
      errors.push('Assist factor must be between 0 and 1');
    }

    if (s.pointsPerSuccess < 1) {
      errors.push('Points per success must be at least 1');
    }

    if (s.prizeDetermination === 'flat' || s.prizeDetermination === 'hybrid') {
      if (s.instantPrizeIndex === undefined || s.instantPrizeIndex < 0) {
        errors.push('Instant prize index is required for flat / hybrid prize determination');
      }
    }

    if (s.prizeDetermination === 'score-threshold' || s.prizeDetermination === 'hybrid') {
      if (!s.scoreThresholds || s.scoreThresholds.length === 0) {
        errors.push('At least one score threshold is required for score-threshold / hybrid mode');
      }
    }

    return errors;
  }

  getConfigSchema(): ConfigSchema {
    return { sections: [] };
  }
}
